import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CircularProgress, Box } from "@mui/material";

import {
  Button as MuiButton,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Chip,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import { spacing } from "@mui/system";
import { baseUrl } from "../../config";
import { Delete, Download, Search, Add } from "@mui/icons-material";
import useAppDispatch from "../../hooks/useAppDispatch";
import useAppSelector from "../../hooks/useAppSelector";
import useAuth from "../../hooks/useAuth";
import { uploadFile, fetchFiles, deleteFile } from "../../redux/slices/file";
import { fetchPostalInvoicesByAssociation } from "../../redux/slices/postalInvoice";
import { format } from "date-fns";
import PDFViewerModal from "../PDFViewer";
import CustomSnackbar from "./SnackBar";
import useSnackbar from "./SnackbarHook";

const Card = styled(MuiCard)(spacing);

const CardContent = styled(MuiCardContent)(spacing);

const Button = styled(MuiButton)(spacing);

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Paper = styled(MuiPaper)(spacing);

type SortKey = "originalname" | "mimetype" | "size" | "createdAt";
type SortOrder = "asc" | "desc";

const HEAD_CELLS: { id: SortKey; label: string }[] = [
  { id: "originalname", label: "Nimi" },
  { id: "mimetype", label: "Tyyppi" },
  { id: "size", label: "Koko (mb)" },
  { id: "createdAt", label: "Luotu" },
];

function compareRows(a: any, b: any, orderBy: SortKey): number {
  const av = a?.[orderBy];
  const bv = b?.[orderBy];
  if (orderBy === "size") {
    return (Number(av) || 0) - (Number(bv) || 0);
  }
  if (orderBy === "createdAt") {
    return new Date(av).getTime() - new Date(bv).getTime();
  }
  return String(av ?? "").localeCompare(String(bv ?? ""), "fi", {
    sensitivity: "base",
  });
}

function FileList({
  onComplete,
  rows,
  handleShowFiles,
  status,
}: {
  onComplete: (success: boolean) => void;
  rows: any[];
  handleShowFiles: (file: any, isOpen: boolean) => void;
  status: string;
}) {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const [uploading, setUploading] = useState(false);
  const [orderBy, setOrderBy] = useState<SortKey>("createdAt");
  const [order, setOrder] = useState<SortOrder>("desc");

  const handleSort = (id: SortKey) => {
    if (orderBy === id) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(id);
      setOrder("asc");
    }
  };

  const sortedRows = React.useMemo(() => {
    const copy = [...(rows || [])];
    copy.sort((a, b) => {
      const cmp = compareRows(a, b, orderBy);
      return order === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, orderBy, order]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const handleFileChange = async (event: any) => {
    const file = event.target.files[0];
    if (file) {
      setUploading(true);
      try {
        await dispatch(
          uploadFile({ file, associationId: associationId })
        ).unwrap();

        // Refetch files after successful upload
        await dispatch(fetchFiles(associationId));

        onComplete(true);
      } catch (error) {
        onComplete(false);
      } finally {
        setUploading(false);
        // Reset file input so the same file can be uploaded again if needed
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const handleDelete = async (row: any) => {
    if (
      !window.confirm(`Haluatko varmasti poistaa tiedoston "
        ${row.originalname}"?`)
    ) {
      return;
    }
    try {
      await dispatch(deleteFile(row.id)).unwrap();
      onComplete(true);
    } catch (error) {
      onComplete(false);
    }
  };

  const handleDownload = async (filename: any) => {
    try {
      const token = localStorage.getItem("accessToken");

      const response = await fetch(`${baseUrl}/files/download/${filename}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Create a blob from the response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Create a link element, set the URL and click it
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename); // Set the file name for download
      document.body.appendChild(link);
      link.click();

      // Cleanup: remove the link and revoke the object URL
      link?.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      onComplete(true);
    } catch (error) {
      console.error("Error downloading the file:", error);
      onComplete(false);
    }
  };

  return (
    <Card mb={6}>
      <CardContent
        pb={1}
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Tiedostot
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<Add />}
          onClick={handleButtonClick}
          disabled={uploading}
        >
          Lisää tiedosto
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
      </CardContent>
      <Paper>
        <Table size={"small"}>
          <TableHead>
            <TableRow>
              {HEAD_CELLS.map((cell) => (
                <TableCell
                  key={cell.id}
                  sortDirection={orderBy === cell.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === cell.id}
                    direction={orderBy === cell.id ? order : "asc"}
                    onClick={() => handleSort(cell.id)}
                  >
                    {cell.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === "loading" || uploading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : sortedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Ei tiedostoja
                </TableCell>
              </TableRow>
            ) : (
              sortedRows.map((row: any) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    {row.originalname}
                    {row._isPostal && (
                      <Chip
                        label="Postilasku"
                        size="small"
                        color="info"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{row.mimetype}</TableCell>
                  <TableCell>
                    {row.size != null
                      ? (row.size / (1024 * 1024)).toFixed(2)
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {format(new Date(row.createdAt), "dd-MM-yyyy HH:mm")}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Lataa">
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(row.filename)}
                      >
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Näytä tiedot">
                      <IconButton
                        size="small"
                        onClick={() => handleShowFiles(row, true)}
                      >
                        <Search fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {!row._isPostal &&
                      (currentUser?.isSuperAdmin ||
                        currentUser?.activeAssociation?.roles?.includes(
                          "ADMIN"
                        )) && (
                        <Tooltip title="Poista">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function Files() {
  const { currentUser } = useAuth();
  const dispatch = useAppDispatch();
  const [didFetch, setDidFetch] = useState(false);
  const { files = [], status = "idle" } = useAppSelector(
    (state) => state.file || {}
  );
  const { byAssociation: postalInvoices = [] } = useAppSelector(
    (state) => state.postalInvoice
  );
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const {
    openSnackbar,
    snackbarMessage,
    snackbarSeverity,
    progress,
    setOpenSnackbar,
    setSnackbarMessage,
    setSnackbarSeverity,
    setProgress,
    duration,
  } = useSnackbar();

  const handleCloseSnackbar = () => setOpenSnackbar(false);

  // const handleOpen = () => {
  //   dispatch(openUserForm());
  // };

  // const handleShowUser = (user: any) => {
  //   dispatch(setUser(user));
  //   dispatch(openUserForm());
  // };

  const handleShowFiles = (file: any, isOpen: boolean) => {
    handleShowInFileModal(file, isOpen);
  };

  const handleShowInFileModal = (file: any, isOpen: boolean) => {
    const filename = file.filename;
    if (!filename) return;

    setPdfUrl(`${baseUrl}/files/preview/${filename}`);
    setPdfOpen(isOpen);
  };

  // const handleEditUser = (user: User | undefined) => {
  //   dispatch(setUser(user));
  //   dispatch(openUserForm());
  // };

  // const handleDelete = (user: User) => {
  //   // TODO confirm
  //   dispatch(deleteUser(user.id));
  // };

  useEffect(() => {
    if (!didFetch && status === "idle" && associationId) {
      dispatch(fetchFiles(associationId));
      dispatch(fetchPostalInvoicesByAssociation(associationId));
      setDidFetch(true);
    }
  }, [dispatch, currentUser, status, didFetch, associationId]);

  const postalRows = postalInvoices
    .filter((inv) => inv.pdf?.filename)
    .map((inv) => ({
      id: `postal-${inv.id}`,
      originalname: inv.number,
      mimetype: "application/pdf",
      size: null,
      createdAt: inv.issuedAt,
      filename: inv.pdf!.filename,
      _isPostal: true,
      _status: inv.status,
    }));

  const allRows = [...files, ...postalRows];

  return (
    <React.Fragment>
      <Helmet title="Tiedostot" />
      <Typography variant="h3" gutterBottom display="inline">
        Tiedostot
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Tiedostot</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <PDFViewerModal
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        fileUrl={pdfUrl ?? ""}
        authToken={localStorage.getItem("accessToken")}
      />

      <FileList
        rows={allRows}
        status={status}
        handleShowFiles={handleShowFiles}
        onComplete={(success: boolean) => {
          setProgress(100);
          if (success) {
            setSnackbarMessage("Tiedosto käsitelty onnistuneesti!");
            setSnackbarSeverity("success");
          } else {
            setSnackbarMessage("Tiedoston käsittely epäonnistui.");
            setSnackbarSeverity("error");
          }
          setOpenSnackbar(true);
        }}
        // handleShowUser={handleShowUser}
        // handleEditUser={handleEditUser}
        // handleDelete={handleDelete}
        // openForm={open}
      />

      <CustomSnackbar
        open={openSnackbar}
        message={snackbarMessage}
        severity={snackbarSeverity}
        progress={progress}
        duration={duration}
        onClose={handleCloseSnackbar}
      />
    </React.Fragment>
  );
}

export default Files;
